import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Formik } from "formik";
import { isEmpty, omit } from "lodash-es";
import moment, { Moment } from "moment";
import { forwardRef, useState } from "react";
import * as Yup from "yup";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import { AppDateTimePicker } from "@/common/components/Datetime/AppDateTimePicker";
import { DATETIME_FORMATS } from "@/common/constants/common";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { TypeHelper } from "@/common/helpers/type";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useEntityInputFormInfo } from "@/common/hooks/entity/useEntityInputFormInfo";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useUserAffiliation } from "@/common/hooks/user/useUserAffiliation";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  AccessoryCheckDto,
  AccessoryReferenceDto,
  AccessoryStatus,
  ContractStage,
  CreateAccessoryCheckDto,
  CreateAccessoryCheckItemDto,
  EntityType,
  UpdateAccessoryCheckDto,
  UpdateAccessoryCheckItemDto,
  VehicleAccessoriesDto,
} from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import GeneralValidationError from "../../Error/GeneralValidationError";
import FileUploader from "../../Files/FileUploader";
import FormActions from "../../Form/FormActions";
import FormikComputedField from "../../Form/Formik/FormikComputedField";
import AppTextArea from "../../Form/Input/AppTextArea";
import AppIcon from "../../Icons/AppIcon";
import AppLink from "../../Link/AppLink";
import AppModal from "../../Modals/AppModal";
import AppModalTitle from "../../Modals/AppModalTitle";
import AppTypography from "../../Text/AppTypography";
import ContractAutocomplete from "../Contract/ContractAutocomplete";
import ContractAutocompleteOrCreate from "../Contract/ContractAutocompleteOrCreate";
import EntityAffiliationInputV2 from "../EntityAffiliation/EntityAffiliationInputV2";
import InferEntityAffiliationFromVehicleFormikComputedField from "../EntityAffiliation/InferEntityAffiliationFromVehicleFormikComputedField";
import GeneralAttachedTagsInput from "../General/GeneralTag/GeneralAttachedTagsInput";
import VehicleAutocompleteOrCreate from "../Vehicle/VehicleAutocompleteOrCreate";
import BaseEntityCreateUpdate, {
  BaseEntityCreateUpdateInheritableProps,
  FormWrapperHandle,
} from "../components/BaseEntityCreateUpdate";
import { BaseEntityCreateUpdateExtras } from "../components/BaseEntityCreateUpdateExtras";

type AccessoryCheckItemInputDtoLocal = CreateAccessoryCheckItemDto &
  UpdateAccessoryCheckItemDto & {
    accessoryRef?: AccessoryReferenceDto;
    initialAttachments?: AccessoryCheckDto["attachments"];
  };

function mapVehicleAccessoriesToAccessoryCheckItemInput(
  vehicleAccessories: VehicleAccessoriesDto | undefined | null,
): AccessoryCheckItemInputDtoLocal[] | undefined {
  return vehicleAccessories?.accessories?.map(
    (x) =>
      ({
        id: undefined,
        accessoryId: x.id,
        contractId: vehicleAccessories?.contract?.id,
        status: undefined,
        initialAttachments: undefined,
        attachments: undefined,
        accessoryRef: x,
      }) satisfies AccessoryCheckItemInputDtoLocal,
  );
}

type DefaultValues = {
  vehicleId?: CreateAccessoryCheckDto["vehicleId"];
  contractId?: CreateAccessoryCheckDto["contractId"];
  departmentIds?: CreateAccessoryCheckDto["departmentIds"];
  locationIds?: CreateAccessoryCheckDto["locationIds"];
  tenantRequestsMeta?: CreateAccessoryCheckDto["tenantRequestsMeta"];
  inspectedAt?: CreateAccessoryCheckDto["inspectedAt"];
};

export type AccessoryCheckCreateUpdateHandle = FormWrapperHandle;

export interface AccessoryCheckCreateUpdateProps extends BaseEntityCreateUpdateInheritableProps<
  AccessoryCheckDto,
  DefaultValues
> {
  accessoryCheckId?: string;
}

export default forwardRef<AccessoryCheckCreateUpdateHandle, AccessoryCheckCreateUpdateProps>(
  function AccessoryCheckCreateUpdate(
    {
      accessoryCheckId,
      defaultValues,
      onCreate,
      onUpdate,
      onSave,
      onCancel,
    }: AccessoryCheckCreateUpdateProps,
    ref,
  ) {
    const formInfo = useEntityInputFormInfo({ entityId: accessoryCheckId });

    const mounted = useMounted();
    const { enqueueSnackbar } = useAppSnackbar();
    const profile = useUserProfile();
    const userAffiliation = useUserAffiliation();

    const isCreate = !accessoryCheckId;
    const isEdit = !!accessoryCheckId;

    const [contractModalOpen, setContractModalOpen] = useState(false);
    const [isAttachmentFilesUploading, setIsAttachmentFilesUploading] = useState(false);

    const accessoryCheckRequest = useApiRequest(
      apiClient.accessoryChecksApi.apiV1AccessoryChecksAccessoryCheckIdGet,
      {
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        accessoryCheckId: accessoryCheckId!,
      },
      {
        deps: [accessoryCheckId],
        skip: !accessoryCheckId,
      },
    );
    const accessoryCheck = accessoryCheckRequest.data;

    const vehicleIdComputed = defaultValues?.vehicleId || accessoryCheck?.vehicle?.id;
    const contractIdComputed = defaultValues?.contractId || accessoryCheck?.contract?.id;

    const vehicleAccessoriesRequest = useApiRequest(
      apiClient.vehiclesApi.apiV1VehiclesVehicleIdAccessoriesGet,
      {
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        vehicleId: vehicleIdComputed!,
        contractId: contractIdComputed || undefined,
      },
      {
        deps: [],
        skip: true, // imperatively called via FormikComputedField
      },
    );
    const vehicleAccessories = vehicleAccessoriesRequest.data;

    useBreadcrumbReplacements({
      waitTimeout: 10_000,
      idBreadcrumb: accessoryCheck && {
        idValue: accessoryCheck.id!,
        newTitle: accessoryCheck.localNumber || "",
      },
    });

    return (
      <BaseEntityCreateUpdate
        entityType={EntityType.AccessoryCheck}
        entityId={accessoryCheckId}
        entity={accessoryCheck}
        entityRequest={accessoryCheckRequest}
        isIniting={userAffiliation.isIniting || (isEdit && vehicleAccessoriesRequest.isLoading)}
      >
        <Formik<
          BaseFormikValues &
            Omit<CreateAccessoryCheckDto, "items"> &
            Omit<UpdateAccessoryCheckDto, "items"> & {
              isAttachmentFilesHaveErrors: boolean;
              initialAttachments: AccessoryCheckDto["attachments"];
              items: AccessoryCheckItemInputDtoLocal[] | undefined;
              submit: string;
            }
        >
          enableReinitialize={false}
          initialValues={{
            isAttachmentFilesHaveErrors: false,
            vehicleId: accessoryCheck?.vehicle?.id || defaultValues?.vehicleId || undefined,
            contractId: accessoryCheck?.contract?.id || defaultValues?.contractId || undefined,
            departmentIds:
              (!isEmpty(defaultValues?.departmentIds) ? defaultValues!.departmentIds : undefined) ||
              (!isEmpty(accessoryCheck?.departmentIds)
                ? accessoryCheck!.departmentIds
                : undefined) ||
              (!isEmpty(userAffiliation.departments)
                ? userAffiliation.departments!.map((d) => d.id!)
                : undefined),
            locationIds:
              (!isEmpty(defaultValues?.locationIds) ? defaultValues!.locationIds : undefined) ||
              (!isEmpty(accessoryCheck?.locationIds) ? accessoryCheck!.locationIds : undefined) ||
              (!isEmpty(userAffiliation.locations)
                ? userAffiliation.locations!.map((l) => l.id!)
                : undefined),
            inspectedAt:
              accessoryCheck?.inspectedAt ||
              defaultValues?.inspectedAt ||
              moment().format(DATETIME_FORMATS.DISPLAY_DATETIME),
            inspector: {
              ...(accessoryCheck?.inspector || {}),
              isCurrentUser: true,
              phoneNumber: accessoryCheck?.inspector?.phoneNumber,
            },
            externalNumber: accessoryCheck?.externalNumber || undefined,
            isUseSupplierBranding: accessoryCheck ? accessoryCheck.isUseSupplierBranding : true,
            notes: accessoryCheck?.notes || undefined,
            initialAttachments: accessoryCheck?.attachments || undefined,
            attachments:
              accessoryCheck?.attachments && accessoryCheck.attachments.length > 0
                ? accessoryCheck.attachments
                : undefined,
            items: isCreate
              ? undefined
              : accessoryCheck?.items?.map((x) => ({
                  ...x,
                  id: x.id,
                  accessoryId: x.accessory?.id,
                  contractId: x?.contractId,
                  status: x.status,
                  initialAttachments: x?.attachments || undefined,
                  attachments:
                    x.attachments && x.attachments.length > 0 ? x.attachments : undefined,
                  accessoryRef: x.accessory,
                })) || mapVehicleAccessoriesToAccessoryCheckItemInput(vehicleAccessories),
            tenantRequestsMeta:
              accessoryCheck?.tenantRequestsMeta || defaultValues?.tenantRequestsMeta || undefined,
            tags: accessoryCheck?.tags || undefined,

            submit: "",
          }}
          validationSchema={Yup.object().shape({
            // vehicleId: Yup.string().required("Vehicle is required"),
            // type: Yup.string().required("Type is required"),
          })}
          onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
            try {
              if (isCreate) {
                const response = await apiClient.accessoryChecksApi.apiV1AccessoryChecksPost({
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  createAccessoryCheckDto: {
                    ...omit(values, ["submit"]),
                    inspectedAt: values.inspectedAt
                      ? moment(values.inspectedAt).utc().format()
                      : undefined,
                  },
                });
                enqueueSnackbar("Accessory check created.", { variant: "success" });
                onCreate && (await onCreate(response.data));
                onSave && (await onSave(response.data));
              } else {
                const response =
                  await apiClient.accessoryChecksApi.apiV1AccessoryChecksAccessoryCheckIdPut({
                    nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                    accessoryCheckId,
                    updateAccessoryCheckDto: {
                      ...omit(values, ["submit"]),
                      inspectedAt: values.inspectedAt
                        ? moment(values.inspectedAt).utc().format()
                        : undefined,
                    },
                  });
                enqueueSnackbar("Accessory check updated.", { variant: "success" });
                onUpdate && (await onUpdate(response.data));
                onSave && (await onSave(response.data));
              }

              if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
              }
            } catch (err: any) {
              if (mounted.current) {
                ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                setStatus({ success: false });
                setSubmitting(false);
              }
            }
          }}
        >
          {(formikProps) => {
            const {
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              setFieldValue,
            } = formikProps;

            return (
              <form noValidate onSubmit={handleSubmit}>
                <BaseEntityCreateUpdateExtras
                  defaultImperativeHandle={{
                    formWrapperRef: ref,
                    formikProps: formikProps,
                  }}
                  guardProps={{
                    formikProps: formikProps,
                  }}
                />

                <InferEntityAffiliationFromVehicleFormikComputedField
                  formInfo={formInfo}
                  values={{
                    vehicleId: values.vehicleId,
                    vehicle: undefined,
                    departmentIds: values.departmentIds,
                    locationIds: values.locationIds,
                  }}
                  onComputed={(computed) => {
                    setFieldValue("vehicle", computed.vehicle);
                    setFieldValue("departmentIds", computed.departmentIds);
                    setFieldValue("locationIds", computed.locationIds);
                  }}
                />

                {/* Compute items based on vehicle */}
                <FormikComputedField<typeof values, Pick<typeof values, "items">>
                  isEnabled={isCreate}
                  deps={[values.vehicleId, values.contractId]}
                  compute={async (params) => {
                    if (params.values.vehicleId) {
                      const newVehicleAccessories =
                        await vehicleAccessoriesRequest.refetchWithNewParams({
                          ...vehicleAccessoriesRequest.requestParams,
                          vehicleId: params.values.vehicleId,
                          contractId: params.values.contractId || undefined,
                        });
                      return {
                        items:
                          mapVehicleAccessoriesToAccessoryCheckItemInput(newVehicleAccessories),
                      };
                    } else {
                      return {
                        items: undefined,
                      };
                    }
                  }}
                  onComputed={(newValue) => {
                    setFieldValue("items", newValue.items);
                  }}
                />

                <FormContentBlock>
                  {/* Tags */}
                  <FormControl margin='dense' fullWidth>
                    <GeneralAttachedTagsInput
                      value={values.tags}
                      onChange={(newValue) => {
                        setFieldValue("tags", newValue);
                      }}
                    />
                    <FormHelperText error>
                      {ValidationHelper.getFormikErrorsAsString(errors.tags, {
                        isIncludeNested: false,
                      })}
                    </FormHelperText>
                  </FormControl>

                  {!isCreate && (
                    <TextField
                      fullWidth
                      disabled
                      label='Inspection number'
                      margin='dense'
                      type='text'
                      value={accessoryCheck?.localNumber}
                      variant='outlined'
                    />
                  )}

                  <FormControl
                    margin='dense'
                    fullWidth
                    error={Boolean(touched.vehicleId && errors.vehicleId)}
                  >
                    <VehicleAutocompleteOrCreate
                      autocompleteProps={{
                        required: true,
                        disabled: !isCreate || !isEmpty(vehicleIdComputed),
                        entityId: values.vehicleId,
                        isPreload: isCreate,
                        onChange: async (newValue) => {
                          setFieldValue(`vehicleId`, newValue?.id);
                          setFieldValue(`vehicle`, newValue);
                          setFieldValue(`contractId`, null);
                          setFieldValue(
                            "departmentIds",
                            values.vehicleId !== newValue?.id
                              ? undefined
                              : ArrayHelper.undefinedIfEmpty(values.departmentIds),
                          );
                          setFieldValue(
                            "locationIds",
                            values.vehicleId !== newValue?.id
                              ? undefined
                              : ArrayHelper.undefinedIfEmpty(values.locationIds),
                          );
                        },
                      }}
                      createFormPlacement='modal'
                      onCreate={async (newValue) => {
                        setFieldValue(`vehicleId`, newValue?.id);
                        setFieldValue(`vehicle`, newValue);
                        setFieldValue(`contractId`, null);
                        setFieldValue(
                          "departmentIds",
                          values.vehicleId !== newValue?.id
                            ? undefined
                            : ArrayHelper.undefinedIfEmpty(values.departmentIds),
                        );
                        setFieldValue(
                          "locationIds",
                          values.vehicleId !== newValue?.id
                            ? undefined
                            : ArrayHelper.undefinedIfEmpty(values.locationIds),
                        );
                      }}
                    />
                    <FormHelperText>{touched.vehicleId && errors.vehicleId}</FormHelperText>
                  </FormControl>

                  {/* Entity affiliation */}
                  <EntityAffiliationInputV2
                    departments={{
                      departmentIds: values.departmentIds,
                      onChange: (departmentIds) => {
                        setFieldValue("departmentIds", departmentIds);
                      },
                      error: errors.departmentIds,
                    }}
                    locations={{
                      locationIds: values.locationIds,
                      onChange: (locationIds) => {
                        setFieldValue("locationIds", locationIds);
                      },
                      error: errors.locationIds,
                    }}
                  />

                  {values.contractId && (
                    <FormControl margin='dense' fullWidth error={Boolean(errors.contractId)}>
                      <ContractAutocompleteOrCreate
                        autocompleteProps={{
                          disabled: true,
                          entityId: values.contractId,
                          isPreload: isCreate,
                          searchFilters: {
                            vehicleId: values.vehicleId || undefined,
                            excludeStage: ContractStage.Draft,
                          },
                          onChange: (newValue) => {
                            setFieldValue(`contractId`, newValue?.id);
                            setFieldValue(`contract`, newValue || undefined);
                          },
                        }}
                        createFormPlacement='modal'
                        onCreate={(newValue) => {
                          setFieldValue(`contractId`, newValue?.id);
                          setFieldValue(`contract`, newValue || undefined);
                        }}
                      />
                      <FormHelperText>{errors.contractId}</FormHelperText>
                    </FormControl>
                  )}

                  <TextField
                    fullWidth
                    error={Boolean(touched.externalNumber && errors.externalNumber)}
                    helperText={touched.externalNumber && errors.externalNumber}
                    label='External number'
                    margin='dense'
                    name='externalNumber'
                    onChange={(newValue) => {
                      setFieldValue("externalNumber", newValue.target.value);
                    }}
                    type='text'
                    value={values?.externalNumber || ""}
                    variant='outlined'
                  />

                  {/* Inspected at */}
                  <FormControl
                    margin='dense'
                    error={Boolean(touched.inspectedAt && errors.inspectedAt)}
                  >
                    <AppDateTimePicker
                      label='Inspected at'
                      value={(values.inspectedAt && moment(values.inspectedAt)) || null}
                      format={DATETIME_FORMATS.DISPLAY_DATETIME}
                      onChange={(newValue: Moment | null) => {
                        setFieldValue("inspectedAt", newValue?.format() || null);
                      }}
                    />
                    <FormHelperText>{touched.inspectedAt && errors.inspectedAt}</FormHelperText>
                  </FormControl>

                  {/* Include supplier branding */}
                  <FormControl margin='dense' fullWidth>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.isUseSupplierBranding}
                          onChange={(e) => setFieldValue(`isUseSupplierBranding`, e.target.checked)}
                        />
                      }
                      label={
                        <Typography component='span' sx={{ display: "flex" }}>
                          Include supplier branding
                        </Typography>
                      }
                    />
                  </FormControl>

                  {/* Accessories */}
                  <Divider sx={{ my: 2 }} />

                  <Stack sx={{ mb: 2 }} direction={{ xxs: "column", md: "row" }} spacing={1}>
                    <Box>
                      <Typography component='div' variant='h6'>
                        Accessories
                      </Typography>

                      {vehicleAccessories?.contract && (
                        <Typography
                          component='div'
                          variant='body1'
                          fontSize='small'
                          color='text.secondary'
                        >
                          Fetched accessories from contract{" "}
                          <AppLink
                            to={ROUTE_PATH.CONTRACT_VIEW(vehicleAccessories.contract.id)}
                            target='_blank'
                          >
                            {vehicleAccessories.contract.externalNumber ||
                              vehicleAccessories.contract.localNumber}
                          </AppLink>
                          .{" "}
                          {isCreate && (
                            <Button
                              variant='text'
                              size='small'
                              color='primary'
                              onClick={() => {
                                setFieldValue(`contractId`, undefined);
                              }}
                            >
                              Reset to vehicle accessories
                            </Button>
                          )}
                          .
                        </Typography>
                      )}
                    </Box>

                    <Stack sx={{ flex: 1 }} direction='row'>
                      {isCreate && (
                        <AppModal
                          fullWidth
                          trigger={
                            <Button
                              sx={{ ml: "auto" }}
                              variant='text'
                              size='small'
                              disabled={!values.vehicleId}
                              onClick={() => setContractModalOpen(true)}
                            >
                              Load accessories from contract
                            </Button>
                          }
                          triggerParams={{
                            isDisableDefaultAction: true,
                          }}
                          open={contractModalOpen}
                          onClose={() => setContractModalOpen(false)}
                        >
                          <FormControl
                            margin='dense'
                            sx={{ pr: "10px", pl: "10px" }}
                            fullWidth
                            error={Boolean(touched.contractId && errors.contractId)}
                          >
                            <AppModalTitle
                              title={"Select contract from which to load accessories"}
                              withClose
                              onClose={() => setContractModalOpen(false)}
                            />
                            <DialogContent>
                              <FormControl margin='normal' fullWidth>
                                <ContractAutocomplete
                                  entityId={values.contractId}
                                  searchFilters={{
                                    vehicleId: values.vehicleId || undefined,
                                  }}
                                  onChange={async (newValue) => {
                                    setFieldValue(`contractId`, newValue?.id || undefined);
                                  }}
                                />
                                <FormHelperText>
                                  {touched.contractId && errors.contractId}
                                </FormHelperText>
                              </FormControl>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                variant='outlined'
                                color='text'
                                onClick={() => {
                                  setContractModalOpen(false);
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant='contained'
                                color='primary'
                                onClick={async () => {
                                  setContractModalOpen(false);
                                }}
                              >
                                Save
                              </Button>
                            </DialogActions>
                          </FormControl>
                        </AppModal>
                      )}
                    </Stack>
                  </Stack>

                  {vehicleAccessoriesRequest.isLoading && <LinearProgress sx={{ my: 1 }} />}

                  {TypeHelper.isEmpty(values.items) && (
                    <Typography component='div' variant='body2'>
                      No accessories specified
                    </Typography>
                  )}

                  {!TypeHelper.isEmpty(values.items) && (
                    <Paper sx={{ p: 1 }}>
                      <Grid container>
                        <Grid size={{ xxs: 12, xs: 12, sm: 12, md: 6, lg: 4 }}>
                          <List sx={{ p: 0 }}>
                            {values.items?.map((item, i) => (
                              <ListItem key={i} sx={{ bgcolor: "background.gray", mb: 1 }}>
                                <ListItemText
                                  primary={item.accessoryRef?.name}
                                  secondary={item.accessoryRef?.description}
                                  primaryTypographyProps={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  secondaryTypographyProps={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                />

                                <Tooltip title='In order' enterDelay={500} enterNextDelay={500}>
                                  <AppIconButton
                                    onClick={() => {
                                      setFieldValue(`items[${i}].status`, AccessoryStatus.InOrder);
                                    }}
                                  >
                                    <AppIcon
                                      of='accessoryInOrder'
                                      color={
                                        item.status === AccessoryStatus.InOrder ? "success" : "text"
                                      }
                                    />
                                  </AppIconButton>
                                </Tooltip>

                                <Tooltip title='Damaged' enterDelay={500} enterNextDelay={500}>
                                  <AppIconButton
                                    onClick={() => {
                                      setFieldValue(`items[${i}].status`, AccessoryStatus.Damaged);
                                    }}
                                  >
                                    <AppIcon
                                      of='accessoryDamaged'
                                      color={
                                        item.status === AccessoryStatus.Damaged ? "error" : "text"
                                      }
                                    />
                                  </AppIconButton>
                                </Tooltip>

                                <Tooltip title='Missing' enterDelay={500} enterNextDelay={500}>
                                  <AppIconButton
                                    edge='end'
                                    onClick={() => {
                                      setFieldValue(`items[${i}].status`, AccessoryStatus.Missing);
                                    }}
                                  >
                                    <AppIcon
                                      of='accessoryMissing'
                                      color={
                                        item.status === AccessoryStatus.Missing ? "error" : "text"
                                      }
                                    />
                                  </AppIconButton>
                                </Tooltip>
                              </ListItem>
                            ))}
                          </List>

                          {errors.items && (
                            <FormHelperText error>
                              {ValidationHelper.getFormikErrorsAsString(errors.items, {
                                isIncludeNested: false,
                              })}
                            </FormHelperText>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <AppTextArea
                    error={Boolean(touched.notes && errors.notes)}
                    fullWidth
                    helperText={touched.notes && errors.notes}
                    mode='notes'
                    margin='dense'
                    name='notes'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.notes}
                    variant='outlined'
                  />

                  <FormControl fullWidth component='fieldset' margin='dense'>
                    <FileUploader
                      multiple
                      defaultFiles={FileItem.createManyFrom(values.initialAttachments)}
                      onChange={(newFiles) => {
                        setFieldValue(
                          `attachments`,
                          FileItem.toManyGeneralAttachmentInputDto(newFiles),
                        );
                      }}
                      onUploadStarted={() => {
                        setIsAttachmentFilesUploading(true);
                      }}
                      onUploadFinished={() => {
                        setIsAttachmentFilesUploading(false);
                      }}
                      onValidationStatusChange={(filesValidationStatus) => {
                        if (filesValidationStatus)
                          setFieldValue(
                            "isAttachmentFilesHaveErrors",
                            Object.values(filesValidationStatus).some((x) => x === false),
                          );
                      }}
                    />
                  </FormControl>

                  <Divider sx={{ my: 2 }} />

                  <FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          disabled
                          checked={values.inspector?.isCurrentUser}
                          onChange={handleChange}
                          name='inspector.isCurrentUser'
                        />
                      }
                      label={
                        <AppTypography
                          decoration={{ variant: "helpText" }}
                          tooltipProps={{
                            title: "Currently logged in user evaluated the damages",
                          }}
                        >
                          Inspected by{" "}
                          {values.inspector?.personName
                            ? `${values.inspector.personName?.firstName} ${values.inspector.personName?.lastName}`
                            : profile?.personName?.name}
                        </AppTypography>
                      }
                    />
                  </FormControl>
                </FormContentBlock>

                <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                <FormActions>
                  <Button
                    color='primary'
                    disabled={isAttachmentFilesUploading || values.isAttachmentFilesHaveErrors}
                    loading={isSubmitting}
                    type='submit'
                    variant='contained'
                  >
                    Save
                  </Button>

                  {onCancel && (
                    <Button
                      variant='outlined'
                      color='text'
                      disabled={isSubmitting}
                      onClick={onCancel}
                    >
                      Cancel
                    </Button>
                  )}
                </FormActions>
              </form>
            );
          }}
        </Formik>
      </BaseEntityCreateUpdate>
    );
  },
);
